"use client"

import { useEffect, useState } from "react"
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material"
import {
  RRHH_CATEGORIAS_NOVEDAD,
  RRHH_TIPOS_CONCEPTO_LIQUIDACION,
  extractErrorMessage,
  getEmpleadoNombreCompleto,
} from "./rrhhUi.js"

const getCurrentPeriod = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
}

const getCurrentDateInput = () => {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

const buildInitialForm = (novedad = null) => ({
  empleado: novedad?.entidad ? {
    idEntidad: novedad.idEntidad,
    entidad: novedad.entidad,
  } : null,
  periodo: novedad?.periodo || getCurrentPeriod(),
  fechaNovedad: novedad?.fechaNovedad || getCurrentDateInput(),
  categoria: novedad?.categoria || "OTRO",
  tipoConcepto: novedad?.tipoConcepto || "HABER",
  descripcion: novedad?.descripcion || "",
  cantidad: novedad?.cantidad ?? "",
  montoUnitario: novedad?.montoUnitario ?? "",
  montoTotal: novedad?.montoTotal ?? "",
  aprobado: novedad?.aprobado !== false,
  observaciones: novedad?.observaciones || "",
})

const ModalRrhhNovedadLiquidacion = ({
  open,
  onClose,
  onSubmit,
  novedad = null,
  empleados = [],
  submitting = false,
}) => {
  const [form, setForm] = useState(buildInitialForm(novedad))
  const [error, setError] = useState(null)
  const isEdit = Boolean(novedad?.id)

  useEffect(() => {
    if (!open) return
    setForm(buildInitialForm(novedad))
    setError(null)
  }, [open, novedad])

  const handleSubmit = async () => {
    if (!form.empleado?.idEntidad || !form.periodo || !form.descripcion) {
      setError("Empleado, período y descripción son requeridos.")
      return
    }

    setError(null)

    try {
      await onSubmit({
        idEntidad: form.empleado.idEntidad,
        periodo: form.periodo,
        fechaNovedad: form.fechaNovedad,
        categoria: form.categoria,
        tipoConcepto: form.tipoConcepto,
        descripcion: form.descripcion,
        cantidad: form.cantidad === "" ? undefined : Number(form.cantidad),
        montoUnitario: form.montoUnitario === "" ? undefined : Number(form.montoUnitario),
        montoTotal: form.montoTotal === "" ? undefined : Number(form.montoTotal),
        aprobado: form.aprobado,
        observaciones: form.observaciones,
      })
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo guardar la novedad."))
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Editar novedad" : "Nueva novedad"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Autocomplete
            options={empleados}
            value={form.empleado}
            isOptionEqualToValue={(option, value) => option.idEntidad === value.idEntidad}
            getOptionLabel={(option) => {
              const nombre = getEmpleadoNombreCompleto(option)
              const legajo = option?.rrhhEmpleado?.legajo || option?.entidad?.rrhhEmpleado?.legajo
              return legajo ? `${nombre} (${legajo})` : nombre
            }}
            onChange={(_, value) =>
              setForm((prev) => ({
                ...prev,
                empleado: value,
              }))
            }
            renderInput={(params) => <TextField {...params} label="Empleado" required />}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Período"
              type="month"
              value={form.periodo}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  periodo: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label="Fecha novedad"
              type="date"
              value={form.fechaNovedad}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  fechaNovedad: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              required
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Autocomplete
              fullWidth
              options={RRHH_CATEGORIAS_NOVEDAD}
              value={RRHH_CATEGORIAS_NOVEDAD.find((item) => item.value === form.categoria) || null}
              onChange={(_, value) =>
                setForm((prev) => ({
                  ...prev,
                  categoria: value?.value || "OTRO",
                }))
              }
              renderInput={(params) => <TextField {...params} label="Categoría" />}
            />

            <Autocomplete
              fullWidth
              options={RRHH_TIPOS_CONCEPTO_LIQUIDACION}
              value={RRHH_TIPOS_CONCEPTO_LIQUIDACION.find((item) => item.value === form.tipoConcepto) || null}
              onChange={(_, value) =>
                setForm((prev) => ({
                  ...prev,
                  tipoConcepto: value?.value || "HABER",
                }))
              }
              renderInput={(params) => <TextField {...params} label="Tipo de impacto" />}
            />
          </Stack>

          <TextField
            label="Descripción"
            value={form.descripcion}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                descripcion: event.target.value,
              }))
            }
            required
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Cantidad"
              type="number"
              value={form.cantidad}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  cantidad: event.target.value,
                }))
              }
            />

            <TextField
              fullWidth
              label="Monto unitario"
              type="number"
              value={form.montoUnitario}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  montoUnitario: event.target.value,
                }))
              }
            />

            <TextField
              fullWidth
              label="Monto total"
              type="number"
              value={form.montoTotal}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  montoTotal: event.target.value,
                }))
              }
              helperText="Si lo dejás vacío y cargás cantidad + monto unitario, se calcula solo."
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={form.aprobado}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    aprobado: event.target.checked,
                  }))
                }
              />
            }
            label="Incluir como novedad aprobada"
          />

          <TextField
            label="Observaciones"
            value={form.observaciones}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                observaciones: event.target.value,
              }))
            }
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear novedad"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalRrhhNovedadLiquidacion
